import React, { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/public/PageHero";
import api, { apiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

export default function Contact() {
  const { site } = useSite();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <PageHero overline="Get in Touch" title="Contact Our Team" subtitle="Speak with our client desk about accounts, platforms or partnerships." />
      <section className="py-16 sm:py-24 bg-white">
        <div className="cb-container grid lg:grid-cols-3 gap-10">
          <div className="space-y-6">
            {site.contact_email && <ContactRow Icon={Mail} label="Email" value={site.contact_email} />}
            {site.contact_phone && <ContactRow Icon={Phone} label="Phone" value={site.contact_phone} />}
            {site.address && <ContactRow Icon={MapPin} label="Office" value={site.address} />}
            {!site.contact_email && !site.contact_phone && !site.address && (
              <p className="text-slate-500 text-sm">Contact details can be configured in the admin panel under Site Settings.</p>
            )}
          </div>

          <form onSubmit={submit} className="lg:col-span-2 cb-card p-8 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name" required value={form.name} onChange={set("name")} testid="contact-name" />
              <Field label="Email" type="email" required value={form.email} onChange={set("email")} testid="contact-email" />
              <Field label="Phone" value={form.phone} onChange={set("phone")} testid="contact-phone" />
              <Field label="Subject" value={form.subject} onChange={set("subject")} testid="contact-subject" />
            </div>
            <div>
              <label className="text-sm text-slate-700 mb-1.5 block">Message *</label>
              <textarea required rows={5} value={form.message} onChange={set("message")} className="w-full bg-white border border-slate-200 rounded px-3 py-2.5 text-sm text-[#0B1D40] focus:border-[#D8AE4E]/50 outline-none" data-testid="contact-message" />
            </div>
            <button type="submit" disabled={sending} className="btn-gold px-7 py-3 inline-flex items-center gap-2 disabled:opacity-60" data-testid="contact-submit">
              {sending ? "Sending…" : <>Send Message <Send size={16} /></>}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function ContactRow({ Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded border border-slate-200 flex items-center justify-center gold-text shrink-0"><Icon size={18} /></div>
      <div><p className="text-slate-500 text-xs uppercase tracking-wider">{label}</p><p className="text-[#0B1D40] text-sm mt-1">{value}</p></div>
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-sm text-slate-700 mb-1.5 block">{label}{required && " *"}</label>
      <input type={type} required={required} value={value} onChange={onChange} className="w-full bg-white border border-slate-200 rounded px-3 py-2.5 text-sm text-[#0B1D40] focus:border-[#D8AE4E]/50 outline-none" data-testid={testid} />
    </div>
  );
}
